import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import { useTenantHome } from '@/hooks/use-tenant-home';
import { useIsMobile } from '@/hooks/use-mobile';
import { PREDEFINED_RANGE_VALUES } from '@/hooks/use-metrics-date-filter';
import { getLuxonLocale } from '@/lib/utils';
import { autoHomeGranularity } from '@/lib/home-metrics';
import type { Granularity } from '@/components/sales-chart';
import { PeriodFilter } from '@/components/period/period-filter';
import { GranularityFilter } from '@/components/granularity-filter';
import { AppCard } from '@/components/home/app-card';
import { HomeHeader } from '@/components/home/home-header';
import { HomeKpiTiles } from '@/components/home/home-kpi-tiles';
import { HomeRevenueCard } from '@/components/home/home-revenue-card';
import { NextCampaignCard } from '@/components/home/next-campaign-card';
import { NextStepsCard } from '@/components/home/next-steps-card';
import { RecentCampaignsCard } from '@/components/home/recent-campaigns-card';
import { TodaySoFarCard } from '@/components/home/today-so-far-card';
import { useWhatsNewAutoOpen } from '@/components/whats-new/use-whats-new-auto-open';
import {
  WhatsNewHeaderButton,
  WhatsNewTeaser,
} from '@/components/whats-new/whats-new-teaser';

/**
 * Início (HOME-01…12). From 1180px: top; indicators; [revenue (2/3) | Hoje
 * and Próxima campanha (1/3)]; [Próximos passos | Últimas campanhas | Seu
 * app]. Below 1180px everything in one column in that order; on mobile with
 * the "Nova campanha" FAB. Each block loads and fails on its own (HOME-12).
 */
export default function DashboardPage() {
  const { LL, locale } = useI18nContext();
  const isMobile = useIsMobile();
  const [now] = useState(() => DateTime.now());
  useWhatsNewAutoOpen();

  const [fromDate, setFromDate] = useState(() =>
    DateTime.now().minus({ days: 30 }).startOf('day').toISO()
  );
  const [toDate, setToDate] = useState(() => DateTime.now().toISO());
  const [predefinedDateRange, setPredefinedDateRange] = useState<
    string | undefined
  >('last_30_days');
  const [granularity, setGranularity] = useState<Granularity>('day');

  // Mobile has no granularity control: hour for periods up to 24h, else day.
  const effectiveGranularity: Granularity = isMobile
    ? autoHomeGranularity(fromDate ?? '', toDate ?? '')
    : granularity;

  const range = useMemo(
    () => ({
      from: fromDate ?? '',
      to: toDate ?? '',
      preset: PREDEFINED_RANGE_VALUES.find(
        (value) => value === predefinedDateRange
      ),
    }),
    [fromDate, toDate, predefinedDateRange]
  );

  const metrics = useTenantHome({
    fromDate: range.from,
    toDate: range.to,
    granularity: effectiveGranularity,
  });

  const predefinedDateRanges = useMemo(
    () => [
      {
        label: LL.salesChart.predefinedRanges.today(),
        value: 'today',
        from: DateTime.now().startOf('day').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.yesterday(),
        value: 'yesterday',
        from: DateTime.now().minus({ days: 1 }).startOf('day').toISO(),
        to: DateTime.now().minus({ days: 1 }).endOf('day').toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.weekToDate(),
        value: 'week_to_date',
        from: DateTime.now().startOf('week').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last7Days(),
        value: 'last_7_days',
        from: DateTime.now().minus({ days: 7 }).startOf('day').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.monthToDate(),
        value: 'month_to_date',
        from: DateTime.now().startOf('month').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last30Days(),
        value: 'last_30_days',
        from: DateTime.now().minus({ days: 30 }).startOf('day').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last3Months(),
        value: 'last_3_months',
        from: DateTime.now().minus({ months: 3 }).toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last6Months(),
        value: 'last_6_months',
        from: DateTime.now().minus({ months: 6 }).toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.yearToDate(),
        value: 'year_to_date',
        from: DateTime.now().startOf('year').toISO(),
        to: DateTime.now().toISO(),
      },
      {
        label: LL.salesChart.predefinedRanges.last12Months(),
        value: 'last_12_months',
        from: DateTime.now().minus({ months: 12 }).toISO(),
        to: DateTime.now().toISO(),
      },
    ],
    [LL]
  );

  const dateFilterLabel = useMemo(
    () =>
      predefinedDateRanges?.find((g) => g.value === predefinedDateRange)
        ?.label ||
      `${fromDate && DateTime.fromISO(fromDate).setLocale(getLuxonLocale(locale)).toLocaleString()} - ${
        toDate &&
        DateTime.fromISO(toDate)
          .setLocale(getLuxonLocale(locale))
          .toLocaleString()
      }`,
    [predefinedDateRange, fromDate, toDate, locale, predefinedDateRanges]
  );

  const periodFilter = (
    <PeriodFilter
      fromDate={fromDate}
      setFromDate={setFromDate}
      toDate={toDate}
      setToDate={setToDate}
      predefinedDateRange={predefinedDateRange}
      setPredefinedDateRange={setPredefinedDateRange}
      predefinedDateRanges={predefinedDateRanges}
      defaultDateRange="last_30_days"
    />
  );

  const row = 'grid min-w-0 grid-cols-1 gap-3 min-[1180px]:grid-cols-3';

  return (
    <SidebarLayout
      showNewCampaignFab
      mobileHeader={{ actions: <WhatsNewHeaderButton /> }}
    >
      {/* 12px between all blocks: with the 2-line KPI tiles (HOME-02) the
          "Próximos passos" row still starts within 1440×900. */}
      <div className="flex min-w-0 flex-col gap-3">
        {isMobile && <WhatsNewTeaser variant="home-mobile" />}
        <HomeHeader now={now} periodFilter={periodFilter} />
        <HomeKpiTiles range={range} granularity={effectiveGranularity} />
        <div className={row}>
          <div className="min-w-0 min-[1180px]:col-span-2 [&>*]:h-full">
            <HomeRevenueCard
              query={metrics}
              range={range}
              granularity={effectiveGranularity}
              periodLabel={dateFilterLabel}
              variant={isMobile ? 'compact' : 'full'}
              headerAction={
                isMobile ? undefined : (
                  <GranularityFilter
                    fromDate={fromDate}
                    toDate={toDate}
                    granularity={granularity}
                    setGranularity={setGranularity}
                  />
                )
              }
            />
          </div>
          <div className="flex min-w-0 flex-col gap-3 [&>*:last-child]:flex-1">
            <TodaySoFarCard />
            <NextCampaignCard />
          </div>
        </div>
        <div className={row}>
          <NextStepsCard />
          <RecentCampaignsCard currency={metrics.data?.currency} />
          <AppCard range={range} />
        </div>
      </div>
    </SidebarLayout>
  );
}
